import React, { useMemo, useState } from 'react'

import { Box, Hidden, NoSsr } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import { makeStyles } from '@material-ui/core/styles'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp'
import CloseIcon from '@material-ui/icons/Close'
import MenuIcon from '@material-ui/icons/Menu'

import useWindowSize from 'common/hooks/useWindowSize'
import Image from 'artkive/components/Image'
import OurProductsNavMenu from 'artkive/components/OurProductsNavMenu'
import { GET_BOX_CLICK, NAVIGATION, REFERRAL_MODAL } from 'artkive/constants/tracker/mainNavigation'
import useMobileBreakpoint from 'artkive/hooks/useMobileBreakpoint'
import useReferralCta from 'artkive/hooks/useReferralCta'
import useSale from 'artkive/hooks/useSale'
import routes from 'artkive/utils/routes'

import artkiveLogo from 'images/ecom/nav/artkiveLogo.svg'

import { TrackLink, withClickTracker } from '../Tracker'

import AccentLink from './AccentLink'
import BrandsBanner from './BrandsBanner'
import MobileMenu from './MobileMenu'
import { CTAWrapper, GetStartedButton, InnerToolbar, MenuButton, OuterToolbar, TopBar } from './Navbar.style'
import NavDropdownMenu, { MenuItem } from './NavDropdownMenu'
import SaleBanner from './SaleBanner'

export const MAIN_HEADER_ID = 'main-header'

const useStyles = makeStyles((muiTheme) => ({
  restMenu: {
    justifyContent: 'center',
    marginRight: -12,
    marginLeft: 12,
  },
  getPromoLink: {
    alignSelf: 'center',
    lineHeight: 1.5,
    fontWeight: 500,
    margin: '0 12px',
  },
  logo: {
    width: 120,
    [muiTheme.breakpoints.up('sm')]: {
      width: 140,
    },
    [muiTheme.breakpoints.up('md')]: {
      width: 200,
    },
  },
  logoWrapper: {
    alignItems: 'center',
    flexGrow: 1,
    [muiTheme.breakpoints.up('md')]: {
      justifyContent: 'flex-start',
    },
    '&>$logoBtn': {
      backgroundColor: 'unset',
    },
  },
  logoBtn: {
    paddingLeft: 0,
    [muiTheme.breakpoints.up('md')]: {
      paddingLeft: '12px',
    },
  },
  artkiveBrandLogo: {
    width: '80px',
    [muiTheme.breakpoints.up('md')]: {
      width: '96px',
    },
  },
  cardkiveBrandLogo: {
    width: '99px',
    [muiTheme.breakpoints.up('md')]: {
      width: '124px',
    },
  },
  photokiveBrandLogo: {
    width: '104px',
    [muiTheme.breakpoints.up('md')]: {
      width: '132px',
    },
  },
}))

const TrackAccentLink = withClickTracker(AccentLink)

const navigationOptions = () => {
  return {
    section: '',
  }
}

const useNavMenuAvailableCounter = () => {
  const { width } = useWindowSize()

  return useMemo(() => {
    // how many items should be shown for screen less than given value
    const visible = {
      1060: 3,
      1216: 4,
      1366: 5,
    }

    const entries = Object.entries(visible)
    const last = entries.find(([w]) => w > width)

    return last ? last[1] : 8
  }, [width])
}

const MENU_ITEM_KEY = {
  OUR_PRODUCTS: 'our-products',
  PRICING: 'pricing',
  HOW_IT_WORKS: 'how-it-works',
  OUR_PROCESS: 'our-process',
  ABOUT_US: 'about-us',
  FAQS: 'faqs',
  GIFT_CARDS: 'gift-cards',
  BLOG: 'blog',
  GET_PROMO: 'get-promo',
  MEMBERSHIPS: 'memberships',
}
const visibleMenuItemKeys = [MENU_ITEM_KEY.OUR_PRODUCTS, MENU_ITEM_KEY.PRICING, MENU_ITEM_KEY.GET_PROMO]

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const visibleItemsCount = useNavMenuAvailableCounter()
  const openReferralModal = useReferralCta()
  const classes = useStyles()
  const isMobile = useMobileBreakpoint()
  const { isSale, priceLabel, boxCheckoutUrl } = useSale()

  const menuItems = useMemo(
    () => [
      <MenuItem
        key={MENU_ITEM_KEY.OUR_PRODUCTS}
        trackerEvent={NAVIGATION.KEEPSAKES_CLICK}
        underline='none'
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        component={TrackLink}
      >
        Our Products{' '}
        <Box component='span' display='inline-flex' alignItems='center' ml='4px'>
          {isOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
        </Box>
      </MenuItem>,
      <MenuItem
        key={MENU_ITEM_KEY.PRICING}
        trackerEvent={NAVIGATION.OUR_PROCESS_CLICK}
        underline='none'
        href={routes.pricing}
        component={TrackLink}
      >
        Pricing
      </MenuItem>,
      <NavDropdownMenu
        key={MENU_ITEM_KEY.HOW_IT_WORKS}
        anchor={({ open, ...rest }) => (
          <MenuItem component={Button} {...rest}>
            How It Works
            <Box component='span' display='inline-flex' alignItems='center' ml='4px'>
              {open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
            </Box>
          </MenuItem>
        )}
      >
        <MenuItem
          key={MENU_ITEM_KEY.OUR_PROCESS}
          trackerEvent={NAVIGATION.OUR_PROCESS_CLICK}
          underline='none'
          href={routes.ourProcess}
          component={TrackLink}
        >
          Our Process
        </MenuItem>
        <MenuItem
          key={MENU_ITEM_KEY.ABOUT_US}
          trackerEvent={NAVIGATION.ABOUT_US_CLICK}
          underline='none'
          href={routes.aboutUs}
          component={TrackLink}
        >
          About Us
        </MenuItem>
        <MenuItem
          key={MENU_ITEM_KEY.FAQS}
          trackerEvent={NAVIGATION.FAQS_CLICK}
          underline='none'
          href={routes.faqs}
          component={TrackLink}
        >
          FAQs
        </MenuItem>
      </NavDropdownMenu>,
      <MenuItem
        key={MENU_ITEM_KEY.MEMBERSHIPS}
        underline='none'
        href={routes.plans}
        trackerEvent={NAVIGATION.MEMBERSHIP_CLICK}
        component={TrackLink}
      >
        Memberships
      </MenuItem>,
      <MenuItem key={MENU_ITEM_KEY.GIFT_CARDS} underline='none' href={routes.giftCards} component={TrackLink}>
        Gift Cards
      </MenuItem>,
      <MenuItem
        key={MENU_ITEM_KEY.BLOG}
        trackerEvent={NAVIGATION.BLOG_CLICK}
        underline='none'
        href={routes.blog}
        target='_blank'
        component={TrackLink}
      >
        Blog
      </MenuItem>,
      isSale ? (
        <MenuItem
          key='sale-nav-item'
          underline='none'
          className={classes.getPromoLink}
          href={boxCheckoutUrl}
          trackerEvent={GET_BOX_CLICK}
          trackerOptions={{
            section: 'header_sale_button',
          }}
          component={TrackLink}
        >
          {`${priceLabel} BOX`}
        </MenuItem>
      ) : (
        <TrackAccentLink
          key={MENU_ITEM_KEY.GET_PROMO}
          className={classes.getPromoLink}
          onClick={openReferralModal}
          trackerEvent={REFERRAL_MODAL.CTA_CLICK}
        >
          Get $20
        </TrackAccentLink>
      ),
    ],
    [isOpen, openReferralModal, classes.getPromoLink, isSale, priceLabel, boxCheckoutUrl],
  )

  const filteredMenuItems = useMemo(() => {
    let hiddenMenuItems = []

    if (visibleItemsCount === menuItems.length) {
      return {
        visibleMenuItems: menuItems,
        hiddenMenuItems,
      }
    }

    const minItemsCount = visibleMenuItemKeys.length
    let itemsToAdd = visibleItemsCount - minItemsCount
    const items = menuItems.reduce((acc, item) => {
      const isAlwaysVisible = visibleMenuItemKeys.includes(item.key)
      if (isAlwaysVisible) {
        acc.push(item)
      } else {
        if (itemsToAdd > 0) {
          acc.push(item)
          itemsToAdd -= 1
        } else {
          // NOTE: "How It Works" menu item has nested menu items,
          // that's why we add only nested menu items into hiddenMenuItems for this element
          const isHowItWorksEl = item.key === MENU_ITEM_KEY.HOW_IT_WORKS

          if (isHowItWorksEl) {
            hiddenMenuItems.push(item.props.children)
          } else {
            hiddenMenuItems.push(item)
          }
        }
      }

      return acc
    }, [])

    return {
      visibleMenuItems: items,
      hiddenMenuItems,
    }
  }, [menuItems, visibleItemsCount])

  return (
    <>
      {isSale ? <SaleBanner /> : <BrandsBanner />}
      <TopBar position='sticky' id={MAIN_HEADER_ID}>
        <OuterToolbar onMouseEnter={() => setIsOpen(false)}>
          <NoSsr>
            <InnerToolbar>
              <Hidden mdUp>
                <MenuButton
                  aria-label='menu'
                  edge='end'
                  fontSize='large'
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                >
                  {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
                </MenuButton>
              </Hidden>
              <Box display='flex' className={classes.logoWrapper}>
                <MenuItem
                  href='/'
                  onMouseEnter={() => setIsOpen(false)}
                  trackerEvent={NAVIGATION.LOGO_CLICK}
                  trackerOptions={navigationOptions}
                  component={TrackLink}
                  className={classes.logoBtn}
                >
                  <Image src={artkiveLogo} className={classes.logo} loading='eager' />
                </MenuItem>
              </Box>
              <Hidden smDown>
                <Box display='flex'>{filteredMenuItems.visibleMenuItems}</Box>
              </Hidden>
              <CTAWrapper>
                <GetStartedButton
                  color='primary'
                  href={boxCheckoutUrl}
                  variant={isMobile ? 'outlined' : 'contained'}
                  trackerEvent={GET_BOX_CLICK}
                  trackerOptions={{
                    section: 'header',
                  }}
                >
                  {isSale ? `Get ${priceLabel} Box` : 'Get My Box'}
                </GetStartedButton>
                <Hidden smDown>
                  {visibleItemsCount < menuItems.length && (
                    <NavDropdownMenu
                      key='more-menu'
                      anchor={({ open, ...rest }) => (
                        <MenuItem component={Button} {...rest} className={`${classes.restMenu} test-more-menu`}>
                          <MenuIcon />
                        </MenuItem>
                      )}
                    >
                      {filteredMenuItems.hiddenMenuItems}
                    </NavDropdownMenu>
                  )}
                </Hidden>
              </CTAWrapper>
            </InnerToolbar>
          </NoSsr>
          <Hidden smDown>
            <OurProductsNavMenu isOpen={isOpen} setIsOpen={setIsOpen} />
          </Hidden>
        </OuterToolbar>
      </TopBar>
      <Hidden mdUp>
        <MobileMenu isOpen={mobileMenuOpen} onNavigate={() => setMobileMenuOpen(false)} />
      </Hidden>
    </>
  )
}

export default Navbar
